/**
 * TEF FUNDED — Site Configuration
 * ============================================================
 * Single source of truth for brand identity, contact info,
 * social links, external URLs, and legal text.
 *
 * Components and pages MUST read from this config rather than
 * hardcoding strings. To rebrand or update contact details,
 * edit this file only.
 */

export const siteConfig = {
    brand: {
        name: 'TEF FUNDED',
        shortName: 'TEF',
        legalName: 'TEF FUNDED',
        tagline: 'Premium Prop Trading Firm',
        description:
            'Premium prop trading with competitive spreads, fast execution, and institutional-grade platforms.',
        domain: 'teffunded.com',
        url: 'https://teffunded.com',
        logo: {
            /** Full wordmark (1000×300). Use anywhere a horizontal logo fits — header, footer. */
            wordmark: '/images/logo/logo.png',
            /** Icon-only mark. Use for favicons and small square contexts. */
            icon: '/images/logo/icon.png',
            /** Social share / OG card thumbnail (1536×1024). */
            thumbnail: '/images/thumbnai.png',
            /** Native aspect ratio of the wordmark (used for layout sizing). */
            wordmarkAspect: 1000 / 300,
        },
        founded: 2024,
    },

    contact: {
        email: {
            info: 'info@teffunded.com',
            support: 'support@teffunded.com',
            sales: 'sales@teffunded.com',
            partners: 'partners@teffunded.com',
            careers: 'careers@teffunded.com',
        },
        phone: '+971547472193',
        address: {
            line1: 'Office #210 Al-Nasar Sports Club',
            line2: 'Dubai, UAE',
            city: 'Dubai',
            country: 'UAE',
        },
    },

    social: [
        { label: 'X (Twitter)', href: 'https://x.com/teffunded', icon: 'X' },
        { label: 'Instagram', href: 'https://www.instagram.com/teffunded/', icon: 'IG' },
        { label: 'Facebook', href: 'https://www.facebook.com/teffunded', icon: 'FB' },
        { label: 'LinkedIn', href: 'https://www.linkedin.com/company/teffunded/', icon: 'LI' },
        { label: 'YouTube', href: 'https://www.youtube.com/@teffunded', icon: 'YT' },
        { label: 'Discord', href: 'https://discord.gg/teffunded', icon: 'DC' },
    ],

    /** External client portal (separate app — open in new tab). */
    portal: {
        login: 'https://client.teffunded.com/login',
        register: 'https://client.teffunded.com/register',
        dashboard: 'https://client.teffunded.com',
    },

    /** Partner / affiliate built by us. */
    developer: {
        name: 'Brokeret',
        url: 'https://brokeret.com',
        location: 'Dubai',
    },

    /** Regions where service is not provided. */
    restrictedRegions: [
        'United States',
        'Cuba',
        'Iraq',
        'Myanmar',
        'North Korea',
        'Sudan',
    ],

    /**
     * Trading platform.
     * Page copy reads these values so swapping platforms means editing this
     * block only.
     */
    platform: {
        name: 'MetaTrader 5',
        shortName: 'MT5',
        vendor: 'MetaQuotes',
        downloadUrl: 'https://www.metatrader5.com/en/download',
        /** Native automation language / framework on this platform. */
        automation: {
            name: 'MQL5',
            /** What an automated strategy is called on this platform. */
            botTerm: 'Expert Advisor',
            botTermPlural: 'Expert Advisors',
            botTermShort: 'EA',
            botTermShortPlural: 'EAs',
        },
    },
} as const;

export type SiteConfig = typeof siteConfig;
